import axios from "axios";
import { toast } from "react-toastify";

import * as Sentry from "@sentry/react";
import {
  OnQueryErrorAction,
  OnQuerySuccessAction,
  QueryMeta,
} from "@src/types/react-query";
import { Logout } from "@src/utils";
import { QueryCache, QueryClient } from "@tanstack/react-query";

const MS_IN_HOUR = 60 * 1000 * 60;
const cacheTime = MS_IN_HOUR * 24;

export const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      onError: (error) => {
        if (axios.isAxiosError(error)) {
          toast.error(
            `Something went wrong... ${
              error?.response?.data?.error || error?.message
            }`
          );
          if (!error.response) Sentry.captureException(error);
        }
      },
    },
    queries: {
      retry: 0,
      staleTime: cacheTime,
    },
  },
  queryCache: new QueryCache({
    onSuccess: (data, query) => {
      if (!query.meta) {
        return;
      }

      const queryMeta = query.meta as QueryMeta;
      if (!queryMeta.onSuccess) {
        return;
      }

      const action = queryMeta.onSuccess as OnQuerySuccessAction | undefined;
      if (!action) {
        return;
      }

      if (typeof action === "string") {
        toast.success(action);
        return;
      }

      if (typeof action === "function") {
        action(data);
        return;
      }
    },
    onError: (error, query) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error === "token error") {
          Logout();
          return;
        }

        if (window.location.pathname === "/launch") {
          Logout();
          window.location.reload();
          return;
        }

        const queryMeta = query.meta as QueryMeta | undefined;
        if (!queryMeta || !queryMeta.onError || !error.response?.status) {
          toast.error(`Something went wrong... ${error.response?.data.error}`);
          return;
        }

        const action = (queryMeta.onError[error.response.status] ||
          queryMeta.onError["any"] ||
          undefined) as OnQueryErrorAction | undefined;

        if (action === undefined) {
          toast.error(`Something went wrong... ${error.response?.data.error}`);
          return;
        }

        if (action === "ignore" || action === "") {
          return;
        }

        if (typeof action === "function") {
          action(error);
          return;
        }

        toast.error(action);
      } else {
        // No response from server at all
        Sentry.captureException(error);
      }
    },
  }),
});
